import { RunPaceZone } from '../../types/athlete';

export interface RunWalkBreakdown {
  enabled: boolean;
  ratio: string;
  runMinutes: number;
  walkMinutes: number;
  runPace: string;
  description: string;
  formatBreakdown: (durationMinutes: number) => string;
}

export const parseRunWalkRatio = (ratio: string): { run: number; walk: number } => {
  const parts = ratio.split(':').map(Number);
  return { run: parts[0] || 9, walk: parts[1] || 1 };
};

export const calculateRunWalkPace = (
  easyPacePerKm: string,
  additionalSecondsPerKm: number = 0
): string => {
  const [mins, secs] = easyPacePerKm.split(':').map(Number);
  const totalSeconds = mins * 60 + secs + additionalSecondsPerKm;
  const runMins = Math.floor(totalSeconds / 60);
  const runSecs = totalSeconds % 60;
  return `${runMins}:${runSecs.toString().padStart(2, '0')}`;
};

export const getRunWalkBreakdown = (
  durationMinutes: number,
  zone: RunPaceZone,
  hasRunWalk: boolean = true,
  customRatio?: string
): RunWalkBreakdown => {
  if (!hasRunWalk) {
    return {
      enabled: false,
      ratio: '',
      runMinutes: durationMinutes,
      walkMinutes: 0,
      runPace: zone.perKm,
      description: `Run at ${zone.perKm}/km`,
      formatBreakdown: (mins) => `${mins} min run`,
    };
  }
  
  const ratio = customRatio || '9:1';
  const { run, walk } = parseRunWalkRatio(ratio);
  const cycleMinutes = run + walk;
  const cycles = Math.floor(durationMinutes / cycleMinutes);
  const remainder = durationMinutes % cycleMinutes;
  
  const totalRunMinutes = cycles * run + Math.min(remainder, run);
  const totalWalkMinutes = cycles * walk + Math.max(0, remainder - run);
  
  const runPace = calculateRunWalkPace(zone.perKm);
  
  const breakdown = (mins: number): string => {
    const c = Math.floor(mins / cycleMinutes);
    const r = mins % cycleMinutes;
    const runPart = c * run + Math.min(r, run);
    const walkPart = c * walk + Math.max(0, r - run);
    
    if (walkPart === 0) {
      return `${runPart} min run`;
    }
    return `${runPart} min running + ${walkPart} min walking`;
  };
  
  return {
    enabled: true,
    ratio,
    runMinutes: totalRunMinutes,
    walkMinutes: totalWalkMinutes,
    runPace,
    description: `${run}:${walk} run/walk pattern at ${runPace}/km`,
    formatBreakdown: breakdown,
  };
};

export const getRunWalkDescription = (
  zone: RunPaceZone,
  hasRunWalk: boolean = true,
  ratio: string = '9:1'
): string => {
  if (!hasRunWalk) {
    return `Run at ${zone.perKm}/km (easy pace)`;
  }
  
  const { run, walk } = parseRunWalkRatio(ratio);
  return `${run} min run @ ${zone.perKm}/km, ${walk} min walk break`;
};

export const generateRunWalkGuidance = (zone: RunPaceZone): string => {
  return `
## Run/Walk Strategy

**Your Pace Zones:**
- Easy/Recovery: ${zone.perKm}/km
- Long Run: ${zone.perKm}/km (use this for run portions)

**Recommended Pattern: 9:1**
- Run 9 minutes at easy pace
- Walk 1 minute (use aid stations or timed walk breaks)
- Repeat throughout your run

**Benefits:**
- Reduces cumulative fatigue in muscles
- Allows effective hydration/nutrition intake
- Maintains blood flow and clears metabolic waste
- Mental break every 9 minutes helps motivation
- Enables longer running without injury

**During Training:**
- Practice the 9:1 pattern on all long runs
- Start with shorter intervals (5:1) if needed
- Build up to 9:1 by week 4-6 of training
- Practice during brick runs too

**Race Day:**
- Start run/walk from the beginning
- Use aid stations as walk breaks (every 1-2 miles)
- If struggling, shorten walk breaks to 30-45 seconds
- Trust the process - it works!
  `.trim();
};

export const calculateTotalRunTimeWithWalkBreaks = (
  plannedMinutes: number,
  ratio: string = '9:1'
): { running: number; walking: number; total: number } => {
  const { run, walk } = parseRunWalkRatio(ratio);
  const cycleMinutes = run + walk;
  const cycles = Math.floor(plannedMinutes / cycleMinutes);
  const remainder = plannedMinutes % cycleMinutes;
  
  const running = cycles * run + Math.min(remainder, run);
  const walking = cycles * walk + Math.max(0, remainder - run);
  
  return {
    running,
    walking,
    total: running + walking,
  };
};
